// Japanese Insurance Training Scenarios — Haruka Life
// Follows the same interface as insurance-scenarios.ts

import type { ClientPersona, InsuranceScenario } from './insurance-scenarios';

const harukaPersona: ClientPersona = {
  id: 'tanaka-yuto-001',
  name: 'Tanaka Yuto',
  nameKo: '田中 悠人',
  age: 26,
  gender: 'male',
  occupation: 'Systems Engineer',
  occupationKo: 'システムエンジニア（SE）',
  family: {
    maritalStatus: 'Single',
    maritalStatusKo: '未婚',
    children: 0,
    childrenAges: [],
  },
  financial: {
    annualIncome: '~¥4,200,000',
    annualIncomeKo: '約420万円/年',
    assets: '~¥800,000',
    assetsKo: '約80万円',
  },
  existingCoverage: [
    {
      type: 'Company Health Insurance',
      typeKo: '会社の健康保険',
      provider: 'Employer',
      providerKo: '勤務先',
      coverage: 'Basic coverage only',
      coverageKo: '基本保障のみ',
    },
  ],
  concerns: [
    'hospitalization_cost',
    'income_loss',
    'no_insurance',
    'adult_responsibility',
  ],
  concernsKo: [
    '入院費用の自己負担',
    '収入の減少',
    '保険未加入',
    '社会人としての責任',
  ],
  goals: [
    'Affordable first insurance policy',
    'Cover hospitalization out-of-pocket costs',
    'Build financial discipline',
    'Peace of mind as a working adult',
  ],
  goalsKo: [
    '手頃な最初の保険',
    '入院時の自己負担をカバー',
    '貯蓄習慣を身につける',
    '社会人としての安心感',
  ],
  personality:
    'Reserved, polite, uses desu/masu form, logical but not assertive, indirect deflections',
  personalityKo:
    '控えめで慎重、丁寧語（です・ます調）、論理的だが自己主張は強くない、遠回しな言い回し',
  initialMessage:
    "Hello, I'm Tanaka. Thank you for today. A colleague recommended I come, but honestly I don't really understand insurance yet...",
  initialMessageKo:
    'あ、はい。田中です。今日はよろしくお願いします。あの、同僚に勧められて来たんですけど、正直まだ保険のことはよくわからなくて…',
  avatarUrl: 'https://dopmo1eihgbgm.cloudfront.net/69dfe3576b99428e17706849/abbcccc.png',
  voiceId: 'urE3OJfJRxJuk9kAMN0Y',
};

export const japaneseInsuranceScenarios: Record<string, InsuranceScenario> = {
  'haruka-1': {
    id: 'haruka-japan-001',
    code: 'haruka-1',
    title: 'New Working Adult — First Insurance Consultation',
    titleKo: '新社会人向け 初めての保険相談',
    description:
      'Practice conducting needs-based consultation with a young working adult who has no existing insurance. Recommend medical insurance (primary) and whole life insurance (secondary).',
    descriptionKo:
      '保険未加入の若手社会人とのニーズ型相談をトレーニング。医療保険（主提案）と終身保険（副提案）を提案する。',
    difficulty: 'beginner',
    estimatedDuration: 15,
    learningObjectives: [
      'Conduct needs discovery with a young, insurance-naive client',
      'Use concrete hospitalization cost scenarios to surface needs',
      'Present medical insurance first, then bridge to whole life',
      'Aim for follow-up meeting, not same-day close',
    ],
    learningObjectivesKo: [
      '保険知識のない若年層との効果的なニーズ発掘',
      '具体的な入院費用シナリオでニーズを引き出す',
      'まず医療保険を提案し、終身保険へと繋げる',
      '即日決定ではなく次回面談を目指す',
    ],
    clientPersona: harukaPersona,
    agentId: 'agent_2301kp80tdbkfk7tfexexwfa659h',
    language: 'ja',
    successCriteria: [
      'Client moves from short answers to asking specific questions',
      'Client shares personal concerns voluntarily (hospitalization story)',
      'Client asks about premiums or payment methods',
      'Client agrees to follow-up meeting',
    ],
    successCriteriaKo: [
      'お客様が短い返答から具体的な質問をするようになる',
      'お客様が自発的に個人的な懸念を打ち明ける（入院の話）',
      'お客様が保険料や支払い方法について質問する',
      'お客様が次回面談に同意する',
    ],
  },
};

export function getJapaneseScenarioByCode(
  code: string,
): InsuranceScenario | null {
  return japaneseInsuranceScenarios[code] || null;
}
